import { Badge } from '#root/components/ui/primitives/badge/badge';
import type { ShippingStatusType } from '#root/models/Shipping';
import styles from './shipmentStatusBadge.module.scss';

// Mapeo de status a labels legibles
const variantStatus: Record<string, string> = {
  CREADO: 'Creado',
  REGISTRADO: 'Registrado',
  POR_RECOGER: 'Para recoger...',
  AUTORIZADO: 'Para recoger...',
  RECOGIDO: 'Recogido',
  CONFIRMADO: 'Confirmado',
  PROCESANDO: 'Procesando...',
  READY: 'Listo para despacho...',
  DESPACHADO: 'Enviado',
  PAGADO_COMPRADOR: 'Validando Pago...',
  PAGADO_ADMIN: 'Pagado',
  PAGADO_ORDEN: 'Pagado',
  CERRADO: 'Cerrado',
  BLOQUEADO: 'Bloqueado',
};

// Mapeo de status a mensajes extra de ayuda
const extraStatus: Record<string, string> = {
  REGISTRADO: 'Confirma tu envío',
  POR_RECOGER: 'Acércate a recoger tu pedido con tu Documento de Identidad.',
  AUTORIZADO: 'Acércate a recoger tu pedido con tu Documento de Identidad.',
  RECOGIDO: 'Tú o una persona autorizada por ti, recogió tu pedido.',
  CONFIRMADO: 'Se iniciará el embalaje y procesamiento en breve.',
  PROCESANDO: 'Se está embalando y registrando. Pronto lo podrás pagar.',
  READY: 'Confirma el pago para que pueda ser despachado.',
};

// Mapeo de status a variantes del Badge DS
const statusToVariant: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  CREADO: 'default',
  REGISTRADO: 'warning',
  POR_RECOGER: 'warning',
  AUTORIZADO: 'warning',
  RECOGIDO: 'success',
  CONFIRMADO: 'info',
  PROCESANDO: 'info',
  READY: 'warning',
  DESPACHADO: 'success',
  PAGADO_COMPRADOR: 'info',
  PAGADO_ADMIN: 'success',
  PAGADO_ORDEN: 'success',
  CERRADO: 'default',
  BLOQUEADO: 'error',
};

interface ShipmentStatusBadgeProps {
  status: ShippingStatusType | string;
  paid_at?: string | null;
  shipped_at?: string | null;
  is_closed_debited?: boolean;
  showExtra?: boolean;
}

const ShipmentStatusBadge = ({
  status,
  paid_at,
  shipped_at,
  is_closed_debited = false,
  showExtra = true,
}: ShipmentStatusBadgeProps) => {
  // Determinar el label principal basado en combinación de estados
  let label = variantStatus[status] ?? status;
  let variant = statusToVariant[status] ?? 'default';

  // Lógica especial basada en combinaciones de paid_at/shipped_at
  if (paid_at && shipped_at) {
    label = 'Despachado';
    variant = 'success';
  } else if (paid_at && !shipped_at) {
    label = 'Pagado. Despachandose...';
    variant = 'info';
  } else if (!paid_at && shipped_at) {
    label = 'Falta pagar';
    variant = 'error';
  }

  // Estado especial: cerrado y debitado
  if (is_closed_debited) {
    label = 'Procesado y cerrado';
    variant = 'default';
  }

  // Determinar si necesita badge de acción (rojo)
  const needsAction =
    status === 'REGISTRADO' ||
    status === 'POR_RECOGER' ||
    status === 'READY' ||
    (!paid_at && shipped_at);

  // Obtener mensaje extra si existe
  const extraLabel = showExtra ? extraStatus[status] : undefined;

  return (
    <div className={styles.container}>
      <Badge variant={needsAction ? 'warning' : variant} size="sm">
        {label}
      </Badge>
      {extraLabel && <span className={styles.extraLabel}>{extraLabel}</span>}
    </div>
  );
};

export default ShipmentStatusBadge;
